import { z } from "zod"

/**
 * The deployment's shell configuration, served beside the bundle as `/norn-shell.json` (see
 * ENVIRONMENT.md). It names the cell and the tenant IdP's public OIDC client; it holds no secret.
 * A production bundle requires it; only development and e2e builds may run without it (`DEV_CONNECT`).
 */
const shellConfigSchema = z.object({
  /** The tenant cell this shell serves. */
  cell: z.url(),
  oidc: z
    .object({
      /** The IdP issuer; discovery is read from `<issuer>/.well-known/openid-configuration`. */
      issuer: z.url(),
      /** The shell's public client id (authorization code + PKCE; no client secret exists). */
      clientId: z.string().min(1),
      scope: z.string().min(1).default("openid profile email offline_access"),
      /** Extra authorization request parameters the IdP needs, e.g. `{ "audience": "norn-cell-acme" }`. */
      authorizeParams: z.record(z.string(), z.string()).default({}),
    })
    .strict()
    .optional(),
}).strict()

export type ShellConfig = z.infer<typeof shellConfigSchema>
export type OidcConfig = NonNullable<ShellConfig["oidc"]>

/** Parse a served configuration; a malformed file is an error, never silently development mode. */
export function parseShellConfig(value: unknown): ShellConfig {
  return shellConfigSchema.parse(value)
}

/**
 * Read `/norn-shell.json`. A missing file (404, or a static host's HTML fallback) returns null, which
 * only a development or e2e build accepts; anything else that is not a valid configuration throws.
 */
export async function loadShellConfig(fetchImpl: typeof fetch = fetch): Promise<ShellConfig | null> {
  const response = await fetchImpl("/norn-shell.json", { cache: "no-store", headers: { accept: "application/json" } })
  if (response.status === 404) return null
  if (!response.ok) throw new Error(`/norn-shell.json answered HTTP ${response.status}`)
  if (!(response.headers.get("content-type") ?? "").includes("json")) return null
  return parseShellConfig(await response.json())
}
